import { useMutation, useQuery } from "@tanstack/react-query";

import { dealsApi, productsApi } from "@/api/endpoints";
import type { DealDetail } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { Panel, PanelActions, PanelHead, ActionHint, Spacer } from "@/components/ui/Panel";
import { Tag } from "@/components/ui/Pill";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onNext: () => void;
  onOpenPreview: () => void;
}

export function ProductStep({ deal, readOnly, onChanged, onNext, onOpenPreview }: StepProps) {
  const { t } = useI18n();
  const products = useQuery({ queryKey: ["products"], queryFn: productsApi.list });

  const selectProduct = useMutation({
    mutationFn: (productId: number) => dealsApi.selectProduct(deal.id, productId),
    onSuccess: onChanged,
  });

  const selected = deal.product;
  const signatureLocked = deal.signature_request !== null;
  // readOnly folds in for disabling, but not for the warning below: telling a
  // viewer the signature is out would be a different, untrue reason.
  const locked = readOnly || signatureLocked;

  return (
    <Panel>
      <PanelHead eyebrow={t("product.eyebrow")} title={t("product.title")}>
        {t("product.intro")}
      </PanelHead>

      {signatureLocked && (
        <Note tone="warn" className="mb-4">
          {t("product.locked")}
        </Note>
      )}

      {products.isPending && <p className="text-muted">{t("product.loading")}</p>}
      {products.isError && <Note tone="error">{(products.error as Error).message}</Note>}

      {/* An empty catalogue is a deployment problem, not an empty state the user
          can act on. Saying so beats rendering blank space that looks like a
          broken page. */}
      {products.isSuccess && products.data.length === 0 && (
        <Note tone="warn">
          <b>{t("product.emptyTitle")}</b> {t("product.emptyBody")}
        </Note>
      )}

      <div className="grid gap-3.5 md:grid-cols-2">
        {products.data?.map((product) => {
          const isSelected = selected?.id === product.id;
          const disabled = !product.is_sellable || locked || selectProduct.isPending;

          return (
            <button
              key={product.id}
              type="button"
              disabled={disabled}
              aria-pressed={isSelected}
              onClick={() => selectProduct.mutate(product.id)}
              className={cn(
                "flex flex-col rounded-card border-2 bg-surface p-4.5 text-left transition-all",
                isSelected ? "border-teal shadow-card" : "border-line",
                !disabled && !isSelected && "hover:border-teal/50 hover:shadow-card",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <div className="font-display text-[17px] font-bold">{product.name}</div>
              <p className="mt-1.5 mb-3.5 flex-1 text-[13.5px] text-muted">{product.description}</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13.5px] font-semibold text-navy">{product.price_label}</span>
                <Tag muted={!product.is_sellable}>{product.tag}</Tag>
              </div>
            </button>
          );
        })}
      </div>

      {selectProduct.isError && (
        <Note tone="error" className="mt-4">
          {(selectProduct.error as Error).message}
        </Note>
      )}

      {selected?.has_demo && (
        <Note tone="info" className="mt-4.5">
          {t("product.demoHint")}{" "}
          <Button variant="teal" size="sm" className="ml-1.5" onClick={onOpenPreview}>
            {t("product.openPreview")}
          </Button>
        </Note>
      )}

      <PanelActions>
        <Spacer />
        <ActionHint>
          {selected ? t("product.chosen", { name: selected.name }) : t("product.chooseHint")}
        </ActionHint>
        <Button disabled={!selected} onClick={onNext}>
          {t("product.continue")}
        </Button>
      </PanelActions>
    </Panel>
  );
}
